import * as React from 'react';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import type {FilterObject} from 'nutshell-core/types';

import type {
    EditionUpdateInput,
    EditionFragment as DripEdition,
    EditionStubFragment as DripEditionStub,
} from 'nutshell-graphql-types';

import type {ReportURLParams} from '../../../reports/report-types';

import {AddressModal} from '../../editions/prepare-to-send-modal/onboarding-modals/address-modal';
import {UnsubscribeModal} from '../../editions/prepare-to-send-modal/onboarding-modals/unsubscribe-modal';
import {useCheckAndResetClientConfigsOnMount} from '../../utils/use-check-and-reset-client-configs-on-mount';

import {DripEditionEditMode} from './drip-edition-edit-mode';
import {DripEditionMiniMapMode} from './drip-edition-mini-map-mode';

type OwnProps = ContextRouter;

type Props = OwnProps & {
    edition: DripEdition | null | undefined;
    editionStub: DripEditionStub | null | undefined;
    editionsList: DripEditionStub[];
    errorLoadingEditionsList: string | null | undefined;
    isLoading: boolean;
    isLoadingEditionsList: boolean;
    isSaving: boolean;
    onSendEmail: (toEmailAddress: string[]) => Promise<any>;
    onCreateEdition: (name: string, campaignId: string) => Promise<any>;
    onDuplicateEdition: (editionId: string, campaignId: string, name: string) => Promise<any>;
    onChange: (editionId: string, beeBodyJson: string) => Promise<any>;
    onEditionSave: (saveData: EditionUpdateInput) => Promise<any>;
    dripSequenceId: string;
    editionId: string;
    onSetSelectedEditorTab: (arg1: string) => void;
    generatePath: (arg1: string) => string;
    onEditionChange: (activeTab: string, newEditionId: string) => void;
    activeFilters: FilterObject[];
    onParamChange: (arg1: any, arg2: boolean) => any;
    onParamPush: (arg1: any, arg2: boolean) => any;
    urlParams: ReportURLParams<'contacts'>;
    onRemoveAllFilters: () => void;
};

export function DripSequenceEditionPage(props: Props) {
    const [isEditModeEnabled, setIsEditModeEnabled] = React.useState<boolean>(false);

    // Help modals that may need to be displayed
    const [shouldShowUnsubscribeModal, setShouldShowUnsubscribeModal] =
        React.useState<boolean>(false);
    const [shouldShowAddressModal, setShouldShowAddressModal] = React.useState<boolean>(false);

    useCheckAndResetClientConfigsOnMount([
        {
            name: 'shouldShowUnsubscribeModal',
            action: () => setShouldShowUnsubscribeModal(true),
            timeout: 1000,
        },
        {
            name: 'shouldShowAddressModal',
            action: () => setShouldShowAddressModal(true),
            timeout: 1000,
        },
    ]);

    return isEditModeEnabled && props.edition ? (
        // Shows edition in full-page editor mode (used for editing active editions)
        <DripEditionEditMode
            onDiscardChanges={() => {
                setIsEditModeEnabled(false);
            }}
            onPublishChanges={(publishedChanges: EditionUpdateInput) => {
                return props.onEditionSave(publishedChanges).then(() => {
                    setIsEditModeEnabled(false);
                });
            }}
            edition={props.edition}
        />
    ) : (
        // Shows edition in "regular" view with mini-map
        <>
            <DripEditionMiniMapMode
                {...props}
                edition={props.edition}
                editionStub={props.editionStub}
                editionsList={props.editionsList}
                errorLoadingEditionsList={props.errorLoadingEditionsList}
                isLoading={props.isLoading}
                isLoadingEditionsList={props.isLoadingEditionsList}
                isSaving={props.isSaving}
                onSendEmail={props.onSendEmail}
                onChange={props.onChange}
                onEditionSave={props.onEditionSave}
                dripSequenceId={props.dripSequenceId}
                editionId={props.editionId}
                onSetSelectedEditorTab={props.onSetSelectedEditorTab}
                generatePath={props.generatePath}
                onEditionChange={props.onEditionChange}
                activeFilters={props.activeFilters}
                onParamChange={props.onParamChange}
                onParamPush={props.onParamPush}
                urlParams={props.urlParams}
                onToggleEditMode={() => setIsEditModeEnabled(true)}
            />
            {shouldShowUnsubscribeModal ? (
                <UnsubscribeModal
                    isOpen={shouldShowUnsubscribeModal}
                    onClose={() => {
                        setShouldShowUnsubscribeModal(false);
                    }}
                />
            ) : undefined}
            {shouldShowAddressModal ? (
                <AddressModal
                    isOpen={shouldShowAddressModal}
                    onClose={() => {
                        setShouldShowAddressModal(false);
                    }}
                />
            ) : undefined}
        </>
    );
}
